import { useMemo } from 'react';
import { View, ActivityIndicator } from 'react-native';
import { MapView, type MapPin } from '@/components/maps/MapView';
import { Caption } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import type { LocationCoordinates } from '@/services/location';
import { ShareLocationActionsRow } from './ShareLocationActionsRow';

type LiveShareLocationPreviewProps = {
  coordinate: LocationCoordinates | null;
  /** Stable id when multiple previews mount (e.g. incoming shares list). */
  pinId: string;
  pinTitle?: string;
  /** When true and a coordinate exists, show Copy link and Directions under the map. */
  showShareActions?: boolean;
};

export function LiveShareLocationPreview({
  coordinate,
  pinId,
  pinTitle = 'Shared location',
  showShareActions = false,
}: LiveShareLocationPreviewProps) {
  const theme = useTheme();

  const pins = useMemo((): MapPin[] => {
    if (!coordinate) {
      return [];
    }
    return [
      {
        id: pinId,
        coordinates: coordinate,
        title: pinTitle,
        type: 'user' as const,
      },
    ];
  }, [coordinate, pinId, pinTitle]);

  const outerStyle = useMemo(
    () => ({
      borderRadius: theme.radii.lg ?? 16,
      overflow: 'hidden' as const,
      backgroundColor: theme.colors.surfaceElevated,
    }),
    [theme],
  );

  const rootBottom = { marginBottom: theme.spacing(1.5) };

  if (!coordinate) {
    return (
      <View style={rootBottom}>
        <View
          style={[
            outerStyle,
            {
              height: 160,
              alignItems: 'center',
              justifyContent: 'center',
              gap: theme.spacing(0.75),
            },
          ]}
        >
          <ActivityIndicator size="small" color={theme.colors.primary} />
          <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
            Waiting for location…
          </Caption>
        </View>
      </View>
    );
  }

  return (
    <View style={rootBottom}>
      <View style={outerStyle}>
        <MapView
          pins={pins}
          center={coordinate}
          zoomLevel={14}
          showUserLocation={false}
          showIndexChips={false}
          style={{ height: 160, width: '100%' }}
        />
      </View>
      {showShareActions ? <ShareLocationActionsRow coordinate={coordinate} label={pinTitle} /> : null}
    </View>
  );
}
